import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authService } from "@/services/authService";
import { LoginRequest, ChangePasswordRequest } from "@/types/api";
import { toast } from "sonner";

export const useAuth = () => {
  const queryClient = useQueryClient();

  // Query para verificar se está autenticado
  const { data: isAuthenticated = false, isLoading: isCheckingAuth } = useQuery(
    {
      queryKey: ["auth", "isAuthenticated"],
      queryFn: async () => {
        // Usar a nova função de inicialização
        return await authService.initializeAuth();
      },
      staleTime: 5 * 60 * 1000, // 5 minutos
      retry: false,
    }
  );

  // Query para obter dados do usuário atual
  const { data: currentUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ["auth", "currentUser"],
    queryFn: () => authService.getCurrentUser(),
    enabled: isAuthenticated,
    retry: (failureCount, error) => {
      // Não tentar novamente se for erro 401 (não autorizado)
      if ((error as any)?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Mutation para login
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: async (data) => {
      queryClient.setQueryData(["auth", "isAuthenticated"], true);

      // Buscar dados atualizados do usuário para verificar passwordChangeRequired
      try {
        const userInfo = await authService.getCurrentUser();
        queryClient.setQueryData(["auth", "currentUser"], userInfo);

        // Verificar se o usuário precisa trocar a senha
        if (userInfo.passwordChangeRequired) {
          toast.warning("Você precisa trocar sua senha para continuar");
          // Redirecionar para a página de troca de senha
          window.location.replace("/change-password?required=true");
        } else {
          toast.success("Login realizado com sucesso!");
        }
      } catch (error) {
        // Se falhar ao buscar dados do usuário, continuar com login normal
        toast.success("Login realizado com sucesso!");
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || "Erro ao fazer login";
      toast.error(message);
      console.error("❌ Erro no login:", error);
    },
  });

  // Mutation para logout
  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: (data) => {
      queryClient.setQueryData(["auth", "isAuthenticated"], false);
      queryClient.removeQueries({ queryKey: ["auth", "currentUser"] });
      queryClient.clear(); // Limpa todo o cache
      toast.success(data.message || "Logout realizado com sucesso!");
    },
    onError: (error) => {
      console.error("⚠️ Erro no logout:", error);
      // Mesmo com erro, força o logout local
      queryClient.setQueryData(["auth", "isAuthenticated"], false);
      queryClient.removeQueries({ queryKey: ["auth", "currentUser"] });
      queryClient.clear();
      toast.success("Logout realizado com sucesso!");
    },
  });

  // Mutation para alterar senha
  const changePasswordMutation = useMutation({
    mutationFn: (passwordData: ChangePasswordRequest) =>
      authService.changePassword(passwordData),
    onSuccess: (data) => {
      // Invalidar dados do usuário para buscar informações atualizadas
      queryClient.invalidateQueries({ queryKey: ["auth", "currentUser"] });
      toast.success(data.message || "Senha alterada com sucesso!");
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || "Erro ao alterar senha";
      toast.error(message);
    },
  });

  // Função para forçar revalidação da autenticação
  const revalidateAuth = () => {
    queryClient.invalidateQueries({ queryKey: ["auth", "isAuthenticated"] });
    queryClient.invalidateQueries({ queryKey: ["auth", "currentUser"] });
  };

  // Função para fazer logout manual
  const performLogout = () => {
    logoutMutation.mutate();
  };

  return {
    isAuthenticated,
    currentUser,
    isLoadingUser,
    isCheckingAuth,
    login: loginMutation.mutate,
    logout: performLogout,
    changePassword: changePasswordMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    loginError: loginMutation.error,
    revalidateAuth,
  };
};
